import getMetricInfo from '../../helpers/getMetricInfo';
import { BusinessHealthResponse } from '../../types';
import getLabels from './getLabels';

interface TemplateContext {
    count: number;
    components: string;
    impact: string;
}

type TemplateFn = (ctx: TemplateContext) => string;

const formatComponent = (component: string) => `*${component}*`;

const formatList = (items: string[]): string => {
    if (items.length === 0) return '';
    if (items.length === 1) return formatComponent(items[0]);

    const head = items.slice(0, -1).map(formatComponent);
    const tail = formatComponent(items[items.length - 1]);

    if (head.length === 1) return `${head[0]} and ${tail}`;

    return `${head.join(', ')}, and ${tail}`;
};

export const generateHealthSummaryMarkdown = (
    data?: BusinessHealthResponse,
): string => {
    const components = data?.healthscore?.components;
    if (!components) return '';

    const status = data?.healthscore?.status || '';

    const issues = Object.entries(components)
        .filter(([, c]) => c.included && (c.status === 'at_risk' || c.status === 'critical'))
        .sort((a, b) => (b[1].status === 'critical' ? 1 : 0) - (a[1].status === 'critical' ? 1 : 0))
        .map(([key]) => {
            const labels = getLabels(key, data);
            const metricInfo = getMetricInfo(key);
            // eslint-disable-next-line max-len
            const label = labels?.label || metricInfo?.shortLabel || metricInfo?.label;
            return label ?? key.replace(/_/g, ' ');
        });

    const componentsWithIssues = issues.length;
    const componentsList = formatList(issues);

    const impactMap: Record<string, string> = {
        healthy: 'are holding you back',
        stable: 'could be improved',
        at_risk: 'are impacting your results',
        critical: 'are severely impacting performance',
    };

    const templates: Record<string, TemplateFn> = {
        healthy: ({ count, components: componentsProp, impact }) => (count === 0
            ? 'Your business is in great shape. All key areas are performing well.'
            : `Your business is in great shape, but ${count} ${
                count === 1 ? 'area needs' : 'areas need'
            } attention. ${componentsProp} ${impact}.`),

        stable: ({ count, components: componentsProp }) => (count === 0
            ? 'Your business is stable, with consistent performance across all areas.'
            : `Your business is stable, but ${count} ${
                count === 1 ? 'area could be improved' : 'areas could be improved'
            }: ${componentsProp}.`),

        at_risk: ({ count, components: componentsProp, impact }) => (count <= 2
            ? `Your business is at risk. ${componentsProp} ${impact}.`
            : `Your business is at risk, with several underperforming areas. ${componentsProp} ${impact}.`),

        critical: ({ components: componentsProp, impact }) => `Your business needs immediate attention. ${componentsProp} ${impact}.`,
    };

    const template = templates[status]
    ?? ((ctx: TemplateContext) => `Performance is mixed. ${ctx.components} need attention.`);

    const message = template({
        count: componentsWithIssues,
        components: componentsList,
        impact: impactMap[status] ?? 'need attention',
    });

    return message;
};
